import type { ElementType } from 'react';
import { TbBan, TbCircleCheck, TbFlag } from 'react-icons/tb';
import { brandCopy } from '../../../../constants/brand';

/**
 * The three part allowance lists, shared by the table that shows them and the
 * full-screen flow that adds to them. The flow is reached at
 * /settings/part-allowances/<slug>, so the slug is part of this record rather
 * than derived at either end.
 */

export type PartRuleField = 'allowedPartRules' | 'forbiddenPartRules' | 'flaggedPartRules';

export interface PartRuleList {
    id: PartRuleField;
    /** URL segment of the add/edit flow for this list. */
    slug: string;
    label: string;
    title: string;
    description: string;
    /** One line for a card offering this list as somewhere to move rules to. */
    shortDescription: string;
    emptyText: string;
    icon: ElementType;
    /** Only forbidden rules can prohibit specific insurers. */
    showInsurance: boolean;
}

export const PART_RULE_LISTS: PartRuleList[] = [
    {
        id: 'allowedPartRules',
        slug: 'allowed',
        label: 'Allowed',
        title: 'Allowed Parts',
        description: 'Non-OEM parts listed here will be considered during automated selection.',
        shortDescription: 'Considered during automated selection.',
        emptyText: 'No allowed parts configured. Only OEM parts will be selected.',
        icon: TbCircleCheck as ElementType,
        showInsurance: false,
    },
    {
        id: 'forbiddenPartRules',
        slug: 'forbidden',
        label: 'Forbidden',
        title: 'Forbidden Parts',
        description: 'Parts listed here will be excluded from automated selection.',
        shortDescription: 'Excluded from automated selection.',
        emptyText: 'No forbidden parts configured. All part types will be considered.',
        icon: TbBan as ElementType,
        showInsurance: true,
    },
    {
        id: 'flaggedPartRules',
        slug: 'flagged',
        label: 'Flagged',
        title: 'Flagged Parts',
        description: brandCopy('Parts listed here will be automatically flagged for review when selected by PartBay.'),
        shortDescription: brandCopy('Flagged for review when PartBay selects them.'),
        emptyText: 'No flagged parts configured. No additional parts will be flagged.',
        icon: TbFlag as ElementType,
        showInsurance: false,
    },
];

export const partRuleListBySlug = (slug: string | undefined): PartRuleList | undefined =>
    PART_RULE_LISTS.find((list) => list.slug === slug);

export const partRuleListByField = (field: PartRuleField): PartRuleList =>
    PART_RULE_LISTS.find((list) => list.id === field) ?? PART_RULE_LISTS[0];
